'use client';

import { useState, useCallback } from 'react';
import {
  Question,
  Clock,
  Truck,
  WarningCircle,
  Handshake,
  Package,
  NavigationArrow,
  CaretDown,
  ShoppingBag,
  Info,
  Percent,
  Phone,
  ChatCircle,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

interface FAQQuestion {
  id: string;
  question: string;
  answer: string;
}

interface FAQCategory {
  id: string;
  title: string;
  icon: typeof Question;
  questions: FAQQuestion[];
}

const FAQ_CATEGORIES: FAQCategory[] = [
  {
    id: 'general',
    title: 'Общие вопросы',
    icon: Question,
    questions: [
      {
        id: 'work-hours',
        question: 'В какие часы осуществляются прием и доставка заказов?',
        answer: 'Принимаем и доставляем заказы ежедневно с 11:00 до 22:00.',
      },
      {
        id: 'min-order',
        question: 'Существует ли минимальная сумма заказа?',
        answer: 'Минимальная сумма заказа — 1 000 рублей.',
      },
      {
        id: 'min-order-delivery',
        question: 'Есть ли минимальная сумма заказа для доставки?',
        answer: 'Минимальная сумма заказа для доставки составляет от 1000 до 1999. В зависимости от зоны доставки.',
      },
      {
        id: 'order-options',
        question: 'Какие варианты оформления заказа существуют?',
        answer: 'Все заказы оформляются online на нашем сайте или в приложении. Мы не принимаем заказы по телефону, в соцсетях и мессенджерах. Оплата наличными или банковскими переводами также не предусмотрена.',
      },
      {
        id: 'after-order',
        question: 'Заказ оформлен и оплачен, что делать дальше?',
        answer: 'Если вы оформили заказ и получили уведомление о том, что заказ принят, то просто наберитесь терпения. Совсем скоро мы привезем ваши роллы. Если при оформлении заказа вы выбрали функцию подтверждения заказа, ожидайте звонок нашего оператора с дальнейшими инструкциями.\n\nЕсть срочный вопрос? Звоните по номеру телефона: 8 (863) 333-50-50.',
      },
    ],
  },
  {
    id: 'delivery',
    title: 'Доставка',
    icon: Truck,
    questions: [
      {
        id: 'delivery-time',
        question: 'Как скоро я получу свой заказ?',
        answer: 'Среднее время доставки заказа составляет 60-90 минут. В какие-то районы мы успеваем доставить в течение получаса, в другие приходится добираться чуть дольше. Пожалуйста, наберитесь терпения. Мы очень стараемся приехать как можно быстрее!',
      },
      {
        id: 'delivery-areas',
        question: 'Как понять, включен ли мой адрес в зону доставки?',
        answer: 'Пожалуйста, ознакомьтесь с картой доставки на нашем сайте в разделе «Доставка» или проверьте адрес при оформлении заказа в приложении.',
      },
      {
        id: 'courier-contact',
        question: 'Как я могу связаться с курьером?',
        answer: 'Обмен контактными данными с курьерами не предусмотрен внутренними регламентами компании.\n\nЕсли у вас есть срочный вопрос, пожалуйста, свяжитесь с нашим call-центром: 8 (863) 333-50-50.',
      },
      {
        id: 'yandex-courier',
        question: 'Почему приехал не ваш курьер?',
        answer: 'Мы искренне считаем, что залог качественного сервиса — умение делегировать. Именно поэтому доверяем доставку ваших заказов нашим партнерам — специализированным службам доставки. Мы берем на себя все расходы, связанные с доставкой, а также направляем вам уведомление для отслеживания передвижений вашего заказа.',
      },
      {
        id: 'late-delivery',
        question: 'Курьер опаздывает, что делать?',
        answer: 'Мы очень стараемся, чтобы наши вкусные роллы приезжали к вам без задержек. Но от внештатных ситуаций, к сожалению никто не застрахован.\n\nЕсли курьер опаздывает, пожалуйста, свяжитесь с нашим call-центром: 8 (863) 333-50-50. Мы обязательно разберемся в сложившейся ситуации.',
      },
      {
        id: 'missing-items',
        question: 'Заказ доехал не полностью, что делать?',
        answer: 'Если доставленный заказ лишь частично совпадает с тем, который вы оплатили, незамедлительно сообщите нам об этом по телефону 8 (863) 333-50-50. Мы доставим недостающую часть заказа или компенсируем ее.\n\nВажно: свяжитесь с нашим call-центром не позднее 10 минут с момента получения заказа.',
      },
    ],
  },
  {
    id: 'promotions',
    title: 'Акции и скидки',
    icon: Percent,
    questions: [
      {
        id: 'discounts',
        question: 'Какие акции сейчас действуют?',
        answer: 'У нас всегда действуют несколько выгодных акций: статусные роллы, рулетка с подарками, подарок за первый заказ, ролл за отзыв. Подробности по каждой акции вы можете узнать на нашем сайте в разделе «Акции».',
      },
      {
        id: 'roulette',
        question: 'Как воспользоваться рулеткой с подарками?',
        answer: 'При заказе от 1000 на этапе оформления заказа вам будет предложено крутить рулетку. Нажмите кнопку "Крутить" и выиграйте один из 6 возможных роллов.\n\nВыигранный ролл будет автоматически добавлен к вашему заказу за 249 рублей.',
      },
      {
        id: 'promo-codes',
        question: 'У меня есть промокод, как его применить?',
        answer: 'Используйте промокод в специальном окне в момент оформления заказа, или укажите его в комментариях к заказу.',
      },
      {
        id: 'where-promo',
        question: 'Где брать актуальные промокоды и узнавать о новых акциях?',
        answer: 'Информация о бонусах и горячих предложениях доступна участникам секретного чата. Присоединяйтесь в наше сообщество ВКонтакте!',
      },
      // Вопрос про День рождения убран вместе со скрытием самой акции.
    ],
  },
  {
    id: 'product',
    title: 'О продукции',
    icon: ShoppingBag,
    questions: [
      {
        id: 'dislike',
        question: 'Мне не понравились роллы, что делать?',
        answer: 'Чтобы оценить качество приготовленных роллов, нам потребуется возврат порции в количестве не менее 80%. Шеф проведет экспертизу, приготовит новую порцию и мы отправим ее вам повторно.\n\nВажно:\n— срок приготовления определяется текущей плотностью заказов;\n— расчет времени на доставку обменных блюд не производится;\n— вкусовые предпочтения не являются уважительной причиной для возврата продукта, только блюдо ненадлежащего качества.',
      },
      {
        id: 'cold-rolls',
        question: 'Горячие роллы — не горячие. Почему?',
        answer: 'Горячие роллы — это способ приготовления, но не вариант подачи. Мы используем небольшую температурную нагрузку лишь для того, чтобы сформировать шапочки на запеченных роллах, и корочку — на темпурных. Сам ролл и начинка должны оставаться холодными.',
      },
      {
        id: 'rolls-in-soy',
        question: 'Ролл развалился в соевом соусе. Почему?',
        answer: 'Классическая церемония поедания роллов не предполагает их замачивания в соевом соусе. Воздушный рисовый рулетик при намокании и физическом воздействии бамбуковых палочек распадается, руководствуясь принципами физики. Чтобы избежать подобных казусов, лишь слегка обмакните ролл в соус, чтобы оттенить и дополнить вкус.',
      },
      {
        id: 'uneven-cuts',
        question: 'Роллы порезаны неровно. Почему?',
        answer: 'Наши сушисты хоть и огромные профессионалы, но все человеческое им тоже не чуждо. Ручной, а не механический труд, допускает погрешность в нарезке. Если один кусочек на пару миллиметров толще другого, не сомневайтесь — он такой же вкусный!',
      },
      {
        id: 'small-size',
        question: 'Роллы как будто маленькие... Это так?',
        answer: 'Мы работаем по внутренним регламентам компании, а также со строгим соблюдением граммовок, указанных в технологической карте конкретного блюда. Итоговый вес продукта указан в нашем меню. Пожалуйста, будьте внимательны, оформляя заказ!',
      },
    ],
  },
  {
    id: 'other',
    title: 'Прочее',
    icon: Info,
    questions: [
      {
        id: 'allergens',
        question: 'Про аллергены',
        answer: 'Все наши продукты могут содержать следующие аллергены:\n\n— рыба и продукты ее переработки;\n— ракообразные и продукты их переработки;\n— моллюски и продукты их переработки;\n— арахис и продукты его переработки;\n— горчица и продукты ее переработки;\n— кунжут и продукты их переработки;\n— молоко и продукты их переработки (в том числе лактоза);\n— яйца и продукты их переработки;\n— некоторые фрукты и ягоды;\n— орехи и продукты их переработки;\n— мясо курицы;\n— злаки, содержащие глютен, и продукты их переработки;\n— мед.\n\nПожалуйста, убедитесь в отсутствии противопоказаний, оформляя заказ!',
      },
      {
        id: 'why-better',
        question: 'Почему стоит попробовать ваши роллы?',
        answer: '«Азиаточка» — сервис быстрой доставки вкусных роллов. Наши роллы спасают, когда хочется перекусить.\n\nНаши роллы — это баланс вкуса и пользы:\n— сочный рис с правильной заправкой — база;\n— больше морепродуктов, овощей и фруктов;\n— «та самая» нарезка и впечатляющая подача.',
      },
      {
        id: 'management',
        question: 'У меня есть вопрос к руководству, как связаться?',
        answer: 'Будьте уверены, что любое ваше предложение, адресованное нашему руководителю, обязательно будет передано ему. Оставьте свой вопрос оператору нашего call-центра: 8 (863) 333-50-50.',
      },
      {
        id: 'refund',
        question: 'Заказ отменен, как вернуть деньги?',
        answer: 'Возврат денежных средств осуществляется в рамках внутренних правил вашего банка и обычно занимает до 10 рабочих дней.',
      },
    ],
  },
];

export function FAQPage() {
  const [selectedCategory, setSelectedCategory] = useState('general');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggle = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  const currentCategory = FAQ_CATEGORIES.find((c) => c.id === selectedCategory);

  return (
    <>
      {/* Hero */}
      <section className="relative pt-28 pb-12 sm:pt-32 sm:pb-16 bg-gradient-to-b from-[var(--bg-secondary)] to-[var(--bg-primary)] overflow-hidden">
        <div className="blur-blob absolute -top-40 -right-40 w-[500px] h-[500px] rounded-pill bg-[var(--primary)]/10 blur-[100px]" />
        <div className="blur-blob absolute top-1/2 -left-32 w-[400px] h-[400px] rounded-pill bg-[var(--violet-400)]/8 blur-[80px]" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 relative z-10 text-center">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-pill bg-[var(--primary-subtle)] text-[var(--primary)] t-secondary font-semibold mb-4">
              <Question weight="fill" className="w-4 h-4" />
              Центр помощи
            </span>
          </div>
          {/* Витринная типографика заголовка вне 7 ролей: шкала ролей
              заканчивается на 28px, а маркетинговому герою нужно больше. */}
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black leading-[1.05] tracking-tight text-[var(--text-primary)] animate-fade-up mb-4"
            style={{ animationDelay: '80ms' }}
          >
            Вопросы и{' '}
            <span className="text-[var(--primary)]">ответы</span>
          </h1>
          <p
            className="text-lg sm:text-xl text-[var(--text-secondary)] max-w-2xl mx-auto leading-relaxed animate-fade-up"
            style={{ animationDelay: '160ms' }}
          >
            Ответы на самые популярные вопросы о нашей работе и доставке
          </p>
        </div>
      </section>

      {/* Category tabs + Questions */}
      <section className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          {/* Category pills */}
          <div className="flex flex-wrap gap-2 mb-8 animate-fade-up" style={{ animationDelay: '240ms' }}>
            {FAQ_CATEGORIES.map((cat) => {
              const isActive = selectedCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => {
                    setSelectedCategory(cat.id);
                    setExpandedId(null);
                  }}
                  className={cn(
                    'inline-flex items-center gap-2 px-4 py-3 rounded-pill t-secondary font-bold transition-all duration-200',
                    isActive
                      ? 'bg-[var(--primary)] text-white shadow-float'
                      : 'bg-[var(--surface-primary)] text-[var(--text-secondary)] border border-[var(--border-default)] hover:border-[var(--border-hover)] hover:text-[var(--text-primary)]',
                  )}
                >
                  <cat.icon weight="fill" className="w-4 h-4" />
                  {cat.title}
                </button>
              );
            })}
          </div>

          {/* Questions */}
          <div className="space-y-3">
            {currentCategory?.questions.map((item) => {
              const isOpen = expandedId === item.id;
              return (
                <div
                  key={item.id}
                  className={cn(
                    'rounded-card border transition-all duration-200',
                    'bg-[var(--surface-primary)]',
                    isOpen
                      ? 'border-[var(--primary)]/25 shadow-float'
                      : 'border-[var(--border-default)] hover:border-[var(--border-hover)]',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => toggle(item.id)}
                    className="w-full flex items-center gap-4 p-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]/30 rounded-card"
                    aria-expanded={isOpen}
                  >
                    <span
                      className={cn(
                        // Роль t-card-title несёт и вес 600, поэтому font-bold рядом
                        // не ставим: под вариантом sm: роль всё равно победила бы вес,
                        // и начертание прыгало бы на брейкпоинте.
                        'flex-1 t-card-title transition-colors duration-200',
                        isOpen ? 'text-[var(--primary)]' : 'text-[var(--text-primary)]',
                      )}
                    >
                      {item.question}
                    </span>

                    <div
                      className={cn(
                        'w-7 h-7 rounded-pill flex items-center justify-center shrink-0 transition-all duration-200',
                        isOpen ? 'bg-[var(--primary)] rotate-180' : 'bg-[var(--primary-subtle)]',
                      )}
                    >
                      <CaretDown
                        weight="bold"
                        className={cn(
                          'w-3.5 h-3.5 transition-colors duration-200',
                          isOpen ? 'text-white' : 'text-[var(--primary)]',
                        )}
                      />
                    </div>
                  </button>

                  <div
                    className="grid transition-[grid-template-rows] duration-300 ease-out"
                    style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
                  >
                    <div className="overflow-hidden">
                      <div className="px-4 pb-4 pt-0">
                        <div className="border-t border-[var(--primary)]/10 pt-4">
                          <p className="t-body text-[var(--text-secondary)] whitespace-pre-line">
                            {item.answer}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="pb-16 sm:pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="rounded-screen bg-gradient-to-r from-[var(--primary)] to-[var(--violet-600)] p-8 sm:p-12 text-center">
            <div className="w-14 h-14 rounded-pill bg-white/20 border-2 border-white/30 flex items-center justify-center mx-auto mb-4">
              <ChatCircle weight="fill" className="w-7 h-7 text-[var(--text-on-primary)]" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-heading font-black text-[var(--text-on-primary)] mb-3">
              Не нашли ответ?
            </h2>
            <p className="text-[var(--text-on-primary)]/90 text-base sm:text-lg mb-8 max-w-md mx-auto">
              Свяжитесь с нами любым удобным способом и мы с радостью вам поможем
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {/* Цвета остаются в inline-style, а не в классах: рядом в globals.css
                  живёт правило, перекрашивающее кнопки с bg-[var(--primary)],
                  и инлайн гарантирует, что белая плашка на градиенте не поедет. */}
              <a
                href="tel:+78633335050"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-pill font-bold t-secondary hover:opacity-90 transition-opacity duration-150"
                style={{ backgroundColor: 'var(--text-on-primary)', color: 'var(--primary)' }}
              >
                <Phone weight="fill" className="w-5 h-5" />
                8 (863) 333-50-50
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
